import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Add, DeleteOutline } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const BODY_MAX = 1024;
const HEADER_MAX = 60;
const FOOTER_MAX = 60;
const DISPLAY_TEXT_MAX = 20;

const useStyles = makeStyles(theme => ({
  field: {
    marginTop: theme.spacing(1)
  },
  row: {
    display: "flex",
    gap: 4,
    alignItems: "flex-start",
    marginTop: theme.spacing(0.5)
  },
  sectionTitle: {
    marginTop: theme.spacing(1.5),
    display: "block"
  },
  hint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary
  }
}));

const uid = prefix =>
  `${prefix}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const emptyCta = () => ({
  buttonId: null,
  body: "",
  displayText: "",
  url: "",
  header: "",
  footer: ""
});

/**
 * Config UI for the flow "button" node:
 * - reply: up to 3 WhatsApp interactive reply buttons (wait for click)
 * - cta: one Meta CTA URL button (send then advance; no webhook on click)
 */
const ButtonNodeConfig = ({ data, patch }) => {
  const classes = useStyles();
  const buttonMode = data.buttonMode === "cta" ? "cta" : "reply";
  const cta = { ...emptyCta(), ...(data.cta || {}) };
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const { data: list } = await api.get("/cta-url-buttons");
      setCatalog(Array.isArray(list) ? list : []);
    } catch (err) {
      toastError(err);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (buttonMode === "cta") {
      loadCatalog();
    }
  }, [buttonMode, loadCatalog]);

  const setButtonMode = mode => {
    if (mode === "cta") {
      patch({
        buttonMode: "cta",
        cta: {
          ...emptyCta(),
          ...(data.cta || {}),
          body: (data.cta && data.cta.body) || data.text || ""
        }
      });
      return;
    }
    patch({ buttonMode: "reply" });
  };

  const patchCta = partial => {
    patch({
      buttonMode: "cta",
      cta: { ...cta, ...partial }
    });
  };

  const applyCatalogButton = buttonId => {
    if (!buttonId) {
      patchCta({ buttonId: null });
      return;
    }
    const found = catalog.find(b => String(b.id) === String(buttonId));
    if (!found) {
      patchCta({ buttonId: Number(buttonId) || buttonId });
      return;
    }
    patchCta({
      buttonId: found.id,
      body: found.body || "",
      displayText: found.displayText || "",
      url: found.url || "",
      header: found.header || "",
      footer: found.footer || ""
    });
  };

  const setButton = (idx, field, value) => {
    const buttons = [...(data.buttons || [])];
    buttons[idx] = { ...buttons[idx], [field]: value };
    patch({ buttons });
  };

  const addButton = () => {
    const buttons = [...(data.buttons || [])];
    if (buttons.length >= 3) return;
    buttons.push({ id: uid("btn"), label: `Opção ${buttons.length + 1}` });
    patch({ buttons });
  };

  const removeButton = idx => {
    const buttons = [...(data.buttons || [])];
    buttons.splice(idx, 1);
    patch({ buttons });
  };

  return (
    <>
      <TextField
        className={classes.field}
        select
        label={i18n.t("chatFlows.editor.buttonMode")}
        value={buttonMode}
        onChange={e => setButtonMode(e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        helperText={
          buttonMode === "cta"
            ? i18n.t("chatFlows.editor.buttonModeCtaHint")
            : i18n.t("chatFlows.editor.buttonModeReplyHint")
        }
      >
        <MenuItem value="reply">
          {i18n.t("chatFlows.editor.buttonModeReply")}
        </MenuItem>
        <MenuItem value="cta">
          {i18n.t("chatFlows.editor.buttonModeCta")}
        </MenuItem>
      </TextField>

      {buttonMode === "reply" && (
        <>
          <Typography
            variant="caption"
            className={classes.sectionTitle}
          >
            {i18n.t("chatFlows.editor.buttons")} (max 3)
          </Typography>
          {(data.buttons || []).map((b, idx) => (
            <div key={b.id || idx} className={classes.row}>
              <TextField
                label="ID"
                value={b.id}
                onChange={e => setButton(idx, "id", e.target.value)}
                variant="outlined"
                size="small"
                style={{ width: 90 }}
              />
              <TextField
                label={i18n.t("chatFlows.editor.buttonLabel")}
                value={b.label}
                onChange={e => setButton(idx, "label", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />
              <IconButton size="small" onClick={() => removeButton(idx)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={addButton}
            disabled={(data.buttons || []).length >= 3}
            style={{ marginTop: 8 }}
          >
            {i18n.t("chatFlows.editor.addButton")}
          </Button>
        </>
      )}

      {buttonMode === "cta" && (
        <>
          <Typography
            variant="caption"
            className={classes.sectionTitle}
          >
            {i18n.t("chatFlows.editor.ctaSection")}
          </Typography>
          <Typography variant="caption" className={classes.hint} component="div">
            {i18n.t("chatFlows.editor.ctaContinueHint")}
          </Typography>

          <FormControl
            className={classes.field}
            variant="outlined"
            size="small"
            fullWidth
          >
            <InputLabel id="cta-catalog-label">
              {i18n.t("chatFlows.editor.ctaCatalog")}
            </InputLabel>
            <Select
              labelId="cta-catalog-label"
              label={i18n.t("chatFlows.editor.ctaCatalog")}
              value={cta.buttonId != null ? String(cta.buttonId) : ""}
              onChange={e => applyCatalogButton(e.target.value)}
              disabled={catalogLoading}
            >
              <MenuItem value="">
                <em>{i18n.t("chatFlows.editor.ctaCatalogManual")}</em>
              </MenuItem>
              {catalog.map(b => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.name || b.displayText}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {i18n.t("chatFlows.editor.ctaCatalogHint")}
            </FormHelperText>
          </FormControl>

          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.ctaBody")}
            value={cta.body || ""}
            onChange={e => patchCta({ body: e.target.value, buttonId: null })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: BODY_MAX }}
            helperText={`${(cta.body || "").length}/${BODY_MAX}`}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.ctaDisplayText")}
            value={cta.displayText || ""}
            onChange={e =>
              patchCta({ displayText: e.target.value, buttonId: null })
            }
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: DISPLAY_TEXT_MAX }}
            helperText={`${(cta.displayText || "").length}/${DISPLAY_TEXT_MAX}`}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.ctaUrl")}
            value={cta.url || ""}
            onChange={e => patchCta({ url: e.target.value, buttonId: null })}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="https://"
            helperText={i18n.t("chatFlows.editor.ctaUrlHint")}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.ctaHeader")}
            value={cta.header || ""}
            onChange={e => patchCta({ header: e.target.value, buttonId: null })}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: HEADER_MAX }}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.ctaFooter")}
            value={cta.footer || ""}
            onChange={e => patchCta({ footer: e.target.value, buttonId: null })}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: FOOTER_MAX }}
          />
        </>
      )}
    </>
  );
};

export default ButtonNodeConfig;
